import { Box, Button, Row, Stack } from "@justworkshr/milo-core";
import {
  CUSTOMER_IO_CTA_CLICK_EVENT,
  sendCustomerIOEvent,
} from "app/customer-io";
import { oktaAuth, pkceIsEnabled } from "app/okta";
import { useGetCompanyDisplayQuery } from "types/generated/operations";
import styles from "../../InternationalSolutionsPage.module.css";
import ComparisonTable from "../../components/ComparisonTable";
import {
  CF_INTERNATIONAL_SOLUTIONS_CONTRACTORS_LINK,
  GLOBAL_GROWTH_MADE_SIMPLE_EBOOK_LINK,
  GLOBAL_HIRING_GUIDES_LINK,
  JUSTWORKS_EOR_HELP_ARTICLE_LINK,
} from "../../constants";
import globalHiringWebinarImage1 from "../../images/global-hiring-webinar-1.png";
import globalHiringWebinarImage2 from "../../images/global-hiring-webinar-2.png";
import globalHiringWebinarImage3 from "../../images/global-hiring-webinar-3.png";

type ResourceCardProps = {
  imageSrc: string;
  title: string;
  description: string;
  buttonText: string;
  buttonHref: string;
  onCtaClick?: () => void;
};

const ResourceCard = ({
  imageSrc,
  title,
  description,
  buttonText,
  buttonHref,
  onCtaClick,
}: ResourceCardProps) => {
  const { smHead, smFont } = styles;

  return (
    <Box
      border={{
        borderColor: "neutralSubtle",
        borderRadius: "lg",
        borderWidth: "sm",
      }}
    >
      <Row spacing="sm" style={{ height: "170px" }}>
        <img
          style={{
            width: "280px",
            backgroundColor: "black",
            borderRadius: "8px 0 0 8px",
          }}
          src={imageSrc}
        />
        <Stack
          style={{ padding: "24px 12px 24px 0", overflow: "hidden" }}
          spacing="xs"
        >
          <p className={smHead}>{title}</p>
          <p className={smFont}>{description}</p>
          <div>
            <Button
              size="sm"
              as="a"
              href={buttonHref}
              variant="outlined"
              target="_blank"
              rightIcon="external-link"
              onClick={onCtaClick}
            >
              {buttonText}
            </Button>
          </div>
        </Stack>
      </Row>
    </Box>
  );
};

const Resources = () => {
  const { lgHead, mdFont } = styles;

  const accessToken = pkceIsEnabled() ? oktaAuth.getAccessToken() : "";
  const { data: memberData } = useGetCompanyDisplayQuery();

  const triggerCustomerIOEvent = (ctaName: string, nextUrl: string) => {
    sendCustomerIOEvent(
      {
        event_type: CUSTOMER_IO_CTA_CLICK_EVENT,
        member_uuids: [memberData?.authenticatedMember.uuid ?? ""],
        company_uuids: [memberData?.authenticatedMember.company.uuid ?? ""],
        data: {
          current_url: window.location.href,
          cta_name: ctaName,
          next_url: nextUrl,
        },
      },
      accessToken
    );
  };

  const HOW_JUSTWORKS_HELPS_BUTTON_URL =
    "https://youtu.be/-_iaK7m5hTY?si=TA1bLkiV8vaeBJ_E";
  const INTERNATIONAL_HIRING_BUTTON_URL =
    "https://youtu.be/tjMUFmVvTmA?si=ase361_teLKtNFgm";
  const HOW_SMALL_BUSINESSES_BUTTON_URL =
    "https://youtu.be/kz70LAgx40w?si=qBvaK64O8Y_BoeI9";

  return (
    <Stack spacing="lg" style={{ paddingBottom: "32px" }}>
      <Stack spacing="sm">
        <p className={lgHead}>Hear from experts</p>
        <p className={mdFont}>
          Learn how global hiring with Justworks can transform your business.
        </p>
        <ResourceCard
          imageSrc={globalHiringWebinarImage1}
          title="How Justworks Helps Small Businesses Scale International Hiring"
          description="Hear from real customers about why they chose to hire globally, how they navigated challenges, and key results they unlocked."
          buttonText="Watch now"
          buttonHref={HOW_JUSTWORKS_HELPS_BUTTON_URL}
          onCtaClick={() => {
            triggerCustomerIOEvent(
              "eor_eor_how_justworks_helps_button",
              HOW_JUSTWORKS_HELPS_BUTTON_URL
            );
          }}
        />
        <ResourceCard
          imageSrc={globalHiringWebinarImage2}
          title="International Hiring for Small Businesses"
          description="Learn how to eliminate the need for foreign entities and get empowered to confidently grow your team globally."
          buttonText="Watch now"
          buttonHref={INTERNATIONAL_HIRING_BUTTON_URL}
          onCtaClick={() => {
            triggerCustomerIOEvent(
              "eor_eor_international_hiring_button",
              INTERNATIONAL_HIRING_BUTTON_URL
            );
          }}
        />
        <ResourceCard
          imageSrc={globalHiringWebinarImage3}
          title="How Small Businesses are Hiring Internationally (And Why It Works)"
          description="Explore why more small businesses are building global teams, what to consider, and the paths to getting started."
          buttonText="Watch now"
          buttonHref={HOW_SMALL_BUSINESSES_BUTTON_URL}
          onCtaClick={() => {
            triggerCustomerIOEvent(
              "eor_eor_how_small_businesses_button",
              HOW_SMALL_BUSINESSES_BUTTON_URL
            );
          }}
        />
      </Stack>
      <Stack spacing="sm">
        <p className={lgHead}>Compare our global hiring solutions</p>
        <p className={mdFont}>
          Justworks EOR is for full-time international employees. But we also
          support international contractors in over 50 countries for
          shorter-term, more flexible work.{" "}
          <a href={CF_INTERNATIONAL_SOLUTIONS_CONTRACTORS_LINK}>
            Learn about contractors
          </a>
        </p>
        <ComparisonTable />
      </Stack>
      <Stack spacing="sm">
        <p className={lgHead}>Read more</p>
        <p className={mdFont}>
          <a
            href={GLOBAL_HIRING_GUIDES_LINK}
            target="_blank"
            onClick={() => {
              triggerCustomerIOEvent(
                "eor_eor_global_hiring_guides_link",
                GLOBAL_HIRING_GUIDES_LINK
              );
            }}
          >
            Global Hiring Guides
          </a>{" "}
          Market-specific guidance
        </p>
        <p className={mdFont}>
          <a
            href={GLOBAL_GROWTH_MADE_SIMPLE_EBOOK_LINK}
            target="_blank"
            onClick={() => {
              triggerCustomerIOEvent(
                "eor_eor_global_growth_made_simple_ebook_link",
                GLOBAL_GROWTH_MADE_SIMPLE_EBOOK_LINK
              );
            }}
          >
            Global Growth Made Simple: The Power of an EOR
          </a>{" "}
          Downloadable eBook
        </p>
        <p className={mdFont}>
          <a
            href={JUSTWORKS_EOR_HELP_ARTICLE_LINK}
            target="_blank"
            onClick={() => {
              triggerCustomerIOEvent(
                "eor_eor_justworks_employer_of_record_help_center_link",
                JUSTWORKS_EOR_HELP_ARTICLE_LINK
              );
            }}
          >
            Justworks Employer of Record (EOR)
          </a>{" "}
          Help Center article 
        </p>
      </Stack>
    </Stack>
  );
};

export default Resources;
